import { AxiosError } from 'axios';
import i18n from '@/Config/app/i18n';

export enum ErrorCodes {
  ACTIVATED = 'ACTIVATED',
  NOT_ACTIVATED = 'NOT_ACTIVATED',
  UNIQUE = 'unique',
  TOO_MANY_REQUESTS = 'TOO_MANY_REQUESTS',
  INVALID_TOKEN = 'INVALID_TOKEN',
  USE_GOOGLE_SIGNIN = 'USE_GOOGLE_SIGNIN',
  REMOTE_MCP_OAUTH_DISCOVERY_FAILED = 'REMOTE_MCP_OAUTH_DISCOVERY_FAILED',
  REMOTE_MCP_OAUTH_MANUAL_REGISTRATION = 'REMOTE_MCP_OAUTH_MANUAL_REGISTRATION',
  REMOTE_MCP_OAUTH_REGISTRATION_REJECTED = 'REMOTE_MCP_OAUTH_REGISTRATION_REJECTED',
}

export type ErrorMessage = {
  message: string;
  code?: ErrorCodes;
  options?: any;
};

type RemoteMcpOAuthStartError = { code?: string; options?: { http_status?: unknown } | null };

/** The localized toast for a refused Remote MCP OAuth start; unknown codes get the generic copy. */
export function remoteMcpOAuthStartFailureMessage(error?: RemoteMcpOAuthStartError | null): string {
  switch (error?.code) {
    case ErrorCodes.REMOTE_MCP_OAUTH_DISCOVERY_FAILED:
      return i18n.t('remote_mcp:oauth.start_failed_discovery');
    case ErrorCodes.REMOTE_MCP_OAUTH_MANUAL_REGISTRATION:
      return i18n.t('remote_mcp:oauth.start_failed_manual_registration');
    case ErrorCodes.REMOTE_MCP_OAUTH_REGISTRATION_REJECTED: {
      const status = error?.options?.http_status;
      return typeof status === 'number'
        ? i18n.t('remote_mcp:oauth.start_failed_registration_with_status', { status })
        : i18n.t('remote_mcp:oauth.start_failed_registration');
    }
    default:
      return i18n.t('remote_mcp:oauth.start_failed');
  }
}

export type BackendError<Error> = {
  errors: Error;
};

export type Errors = ErrorMessage[];

export type ErrorType<Input> = {
  [K in keyof Input]?: Errors;
} & { root?: Errors };

export type Error<Input> = AxiosError<BackendError<ErrorType<Input>>>;

export default <Input extends ErrorType<Input>>(
  errors: Input,
  callback: (field: keyof Input, error: string, code?: ErrorCodes, options?: any) => void,
) => {
  Object.entries(errors).forEach((entry) => {
    const key = entry[0] as keyof Input;
    const value = entry[1] as Errors;
    value.forEach(({ message, code, options }) => {
      callback(key, message, code, options);
    });
  });
};
